import { listOrganizations } from '@jq4race/api-client';
import { Trans, useTranslation } from '@jq4race/i18n';
import type { OrganizationResponse } from '@jq4race/shared';
import {
  Badge,
  Button,
  buttonVariants,
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { Link } from '@tanstack/react-router';
import {
  Building2,
  CheckCircle2,
  CreditCard,
  Mail,
  Plus,
  TriangleAlert,
  UserPlus,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { AccessDeniedState } from '../../../../shared/errors/AccessDeniedState';
import { isForbiddenError } from '../../../../shared/errors/isForbiddenError';
import { RestrictedMeta } from '../../../../shared/errors/RestrictedMeta';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useAutoDismissFeedback } from '../../../../shared/hooks/useAutoDismissFeedback';
import { enumLabel } from '../../../../shared/i18n/enum-label';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { BILLING_STATUS_LABEL_KEYS, BILLING_STATUS_TONE } from '../billing-status';
import { AssignPlanDialog } from '../components/AssignPlanDialog';
import { InviteOrgAdminDialog } from '../components/InviteOrgAdminDialog';

const STATUS_TONE: Record<OrganizationResponse['status'], 'success' | 'secondary' | 'danger'> = {
  active: 'success',
  suspended: 'secondary',
  deleted: 'danger',
};

type InvitationStatus = NonNullable<OrganizationResponse['firstAdminInvitationStatus']>;

const INVITE_STATUS_LABEL_KEYS: Record<InvitationStatus, string> = {
  pending: 'list.inviteStatus.pending',
  expired: 'list.inviteStatus.expired',
  used: 'list.inviteStatus.used',
};

const INVITE_STATUS_TONE: Record<InvitationStatus, 'warning' | 'danger' | 'success'> = {
  pending: 'warning',
  expired: 'danger',
  used: 'success',
};

// billing-status-visibility — Organization.status ("Estado") and
// Subscription.status ("Cobro") are TWO INDEPENDENT state machines (see
// SubscriptionAccessGuard's header): a client reported not being able to
// tell, from this exact screen, whether an org that reads "Activo" is
// actually current on its bill. Rendered as its own labeled badge (never
// merged into the org-status badge above it) so the two can never be
// mistaken for one another. Tone/label maps live in ../billing-status.ts,
// shared with OrganizationDetailPage.

export function OrganizationsPage() {
  const { t, i18n } = useTranslation('organizations');
  const [orgs, setOrgs] = useState<OrganizationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // See EventsPage.tsx for the confirmed production incident this guards
  // against: a 403 (role changed mid-session) must not render like "no
  // organizations", and its banner must not auto-dismiss.
  const [accessDenied, setAccessDenied] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [invitingOrg, setInvitingOrg] = useState<OrganizationResponse | null>(null);
  // saas-monetization-plans v2 — WU-L: assign-plan dialog
  const [assigningPlanOrg, setAssigningPlanOrg] = useState<OrganizationResponse | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const res = await listOrganizations();
      setOrgs(res.organizations);
    } catch (e) {
      setAccessDenied(isForbiddenError(e));
      setError(translateApiError(e, t, { fallback: t('list.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useAutoDismissFeedback({
    error,
    success,
    isErrorPersistent: accessDenied,
    onDismissError: () => setError(null),
    onDismissSuccess: () => setSuccess(null),
  });

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('list.eyebrow')}
        title={t('list.title')}
        description={t('list.description')}
        meta={
          <p className="text-[13px] text-jq-graphite">
            <RestrictedMeta loading={loading && orgs.length === 0} accessDenied={accessDenied}>
              <Trans
                t={t}
                i18nKey="list.count"
                count={orgs.length}
                values={{ count: orgs.length }}
                components={{
                  strong: <span className="font-medium tabular-nums text-jq-charcoal" />,
                }}
              />
            </RestrictedMeta>
          </p>
        }
        actions={
          <Link to="/super-admin/organizations/new" className={buttonVariants({ size: 'sm' })}>
            <Plus className="h-4 w-4" strokeWidth={1.75} />
            {t('list.newOrganization')}
          </Link>
        }
      />

      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {accessDenied ? (
            <AccessDeniedState />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('list.columns.name')}</TableHead>
                  <TableHead>{t('list.columns.legalName')}</TableHead>
                  <TableHead>{t('list.columns.niche')}</TableHead>
                  <TableHead>{t('list.columns.country')}</TableHead>
                  <TableHead>{t('list.columns.status')}</TableHead>
                  <TableHead>{t('list.columns.inviteStatus')}</TableHead>
                  <TableHead className="text-right">{t('list.columns.commission')}</TableHead>
                  <TableHead className="text-right">{t('list.columns.created')}</TableHead>
                  <TableHead className="w-[180px] text-right">
                    {t('list.columns.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && orgs.length === 0 ? (
                  <SkeletonRows />
                ) : orgs.length === 0 ? (
                  <TableEmpty colSpan={9}>
                    <EmptyState />
                  </TableEmpty>
                ) : (
                  orgs.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell>
                        <Link
                          to="/super-admin/organizations/$orgId"
                          params={{ orgId: org.id }}
                          className="font-medium text-jq-charcoal underline-offset-2 hover:underline"
                        >
                          {org.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-jq-graphite">{org.legalName ?? '—'}</TableCell>
                      <TableCell>{enumLabel(t, 'niche', org.niche)}</TableCell>
                      <TableCell className="text-jq-graphite">{org.country ?? '—'}</TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1.5">
                          <Badge variant={STATUS_TONE[org.status]} className="capitalize">
                            {enumLabel(t, 'orgStatus', org.status)}
                          </Badge>
                          <BillingStatusIndicator org={org} />
                        </div>
                      </TableCell>
                      <TableCell>
                        {org.firstAdminInvitationStatus ? (
                          <Badge variant={INVITE_STATUS_TONE[org.firstAdminInvitationStatus]}>
                            <Mail className="mr-1 h-3 w-3" strokeWidth={1.75} />
                            {t(INVITE_STATUS_LABEL_KEYS[org.firstAdminInvitationStatus])}
                          </Badge>
                        ) : (
                          <span className="text-jq-graphite/40 text-[13px]">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-jq-graphite">
                        {formatBps(org.platformFeeBasisPoints)}
                      </TableCell>
                      <TableCell className="text-right text-jq-graphite tabular-nums">
                        {formatDate(org.createdAt, i18n.language)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setInvitingOrg(org)}
                            disabled={org.status !== 'active'}
                          >
                            <UserPlus className="h-4 w-4" strokeWidth={1.75} />
                            {t('list.inviteAdmin')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setAssigningPlanOrg(org)}
                            disabled={org.status !== 'active'}
                          >
                            <CreditCard className="h-4 w-4" strokeWidth={1.75} />
                            {t('list.assignPlan')}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <InviteOrgAdminDialog
        org={invitingOrg}
        onOpenChange={(open) => {
          if (!open) setInvitingOrg(null);
        }}
        onInvited={(email) => {
          setSuccess(t('list.invitationSent', { email }));
        }}
      />

      {/* saas-monetization-plans v2 — WU-L: assign-plan dialog */}
      <AssignPlanDialog
        org={assigningPlanOrg}
        onOpenChange={(open) => {
          if (!open) setAssigningPlanOrg(null);
        }}
        onAssigned={(result) => {
          setOrgs((prev) =>
            prev.map((o) =>
              o.id === assigningPlanOrg?.id
                ? { ...o, platformFeeBasisPoints: result.platformFeeBasisPoints }
                : o,
            ),
          );
          setSuccess(t('list.planAssigned', { name: assigningPlanOrg?.name }));
          // The list row's plan/billing column comes from the list endpoint, not from
          // the assign response: refetch or it keeps saying "No plan" until a reload.
          void reload();
        }}
      />
    </div>
  );
}

// billing-status-visibility — renders the EFFECTIVE subscription status
// (accounts for an elapsed past_due grace window / onboarding deadline —
// see resolveEffectiveSubscriptionStatus on the backend) plus the plan
// name, always under its own "Cobro"/"Billing" caption so it reads as a
// SEPARATE fact from the Organization.status badge above it. `null` means
// the org never had a plan assigned yet — a normal, non-alarming state,
// shown as a neutral placeholder rather than an empty gap.
function BillingStatusIndicator({ org }: { org: OrganizationResponse }) {
  const { t } = useTranslation('organizations');
  const status = org.effectiveSubscriptionStatus;

  if (!status) {
    return <span className="text-[11px] text-jq-graphite/60">{t('list.billing.noPlan')}</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-jq-graphite/70">
        {t('list.billing.label')}
      </span>
      <Badge variant={BILLING_STATUS_TONE[status]}>{t(BILLING_STATUS_LABEL_KEYS[status])}</Badge>
      {org.planName && <span className="text-[11px] text-jq-graphite">{org.planName}</span>}
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
        <TableRow key={`skeleton-${i}`}>
          {Array.from({ length: 9 }).map((_, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
            <TableCell key={`cell-${j}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation('organizations');
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <Building2 className="h-10 w-10 text-jq-stone" strokeWidth={1.25} />
      <p className="text-[14px] font-medium text-jq-charcoal">{t('list.empty.title')}</p>
      <p className="max-w-sm text-[13px] text-jq-graphite">{t('list.empty.description')}</p>
      <Link
        to="/super-admin/organizations/new"
        className={`${buttonVariants({ size: 'sm' })} mt-2`}
      >
        <Plus className="h-4 w-4" strokeWidth={1.75} />
        {t('list.empty.cta')}
      </Link>
    </div>
  );
}

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('organizations');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('list.dismissMessage')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** Format basis points as percentage string, e.g. 500 → "5.00%" */
function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}
